import type { Api, NotificationCampaign, NotificationItem } from "../types";

const now = () => new Date().toISOString();
const wait = () => new Promise((resolve) => setTimeout(resolve, 120));

let inbox: NotificationItem[] = [
	{
		id: "ndl_mock_admin_1",
		campaignId: "ncp_mock_admin_1",
		kind: "admin_message",
		category: "system",
		title: "Добро пожаловать в центр уведомлений",
		body: "Здесь отображаются сообщения, адресованные вашей учётной записи.",
		data: {},
		createdAt: now(),
	},
];

let campaigns: NotificationCampaign[] = [];

const stats = { targeted: 0, materialized: 0, read: 0, unread: 0, failed: 0 };

export const notifications: Api["notifications"] = {
	list: async ({ cursor, limit = 20, filter = "all" }) => {
		await wait();
		const start = cursor ? Number(cursor) : 0;
		const source = filter === "unread" ? inbox.filter((item) => !item.readAt) : inbox;
		const items = source.slice(start, start + limit);
		const next = start + items.length;
		return { items, nextCursor: next < source.length ? String(next) : null, hasMore: next < source.length };
	},
	detail: async (id) => {
		await wait();
		const item = inbox.find((entry) => entry.id === id);
		if (!item) throw new Error("Notification not found");
		return item;
	},
	unreadCount: async () => ({ count: inbox.filter((item) => !item.readAt).length }),
	markRead: async (id) => {
		inbox = inbox.map((item) => item.id === id ? { ...item, readAt: item.readAt ?? now() } : item);
	},
	markAllRead: async () => {
		const readAt = now();
		inbox = inbox.map((item) => ({ ...item, readAt: item.readAt ?? readAt }));
	},
};

export const notificationCampaigns: Api["notificationCampaigns"] = {
	list: async ({ cursor, limit = 25, status } = {}) => {
		await wait();
		const start = cursor ? Number(cursor) : 0;
		const source = status ? campaigns.filter((campaign) => campaign.status === status) : campaigns;
		const items = source.slice(start, start + limit);
		const next = start + items.length;
		return { items, nextCursor: next < source.length ? String(next) : null, hasMore: next < source.length };
	},
	get: async (id) => {
		await wait();
		const campaign = campaigns.find((entry) => entry.id === id);
		if (!campaign) throw new Error("Campaign not found");
		return campaign;
	},
	create: async (input) => {
		const timestamp = now();
		const campaign: NotificationCampaign = {
			id: `ncp_mock_${Date.now()}`,
			kind: "admin_message",
			category: input.category,
			content: input.content,
			data: input.data ?? {},
			action: input.action,
			audience: input.audience,
			status: "draft",
			availableAt: input.availableAt ?? timestamp,
			createdByUserId: "usr_seed_admin",
			stats,
			createdAt: timestamp,
			updatedAt: timestamp,
		};
		campaigns = [campaign, ...campaigns];
		return campaign;
	},
	update: async (id, input) => {
		const campaign = await notificationCampaigns.get(id);
		const updated = { ...campaign, ...input, updatedAt: now() } as NotificationCampaign;
		campaigns = campaigns.map((entry) => entry.id === id ? updated : entry);
		return updated;
	},
	preview: async (id, locale = "ru") => {
		const campaign = await notificationCampaigns.get(id);
		const eligibleCount = campaign.audience.kind === "individual" ? 1 : campaign.audience.kind === "all_users" ? 170 : campaign.audience.kind === "all_buyers" ? 128 : 42;
		return {
			campaignId: id,
			previewToken: `npt_mock_${Date.now()}`,
			expiresAt: new Date(Date.now() + 600_000).toISOString(),
			eligibleCount,
			sample: [{ id: "usr_mock", name: "Mock recipient", email: "recipient@example.test" }],
			content: campaign.content[locale] ?? campaign.content.ru,
		};
	},
	send: async (id) => {
		const campaign = await notificationCampaigns.get(id);
		const count = campaign.audience.kind === "individual" ? 1 : campaign.audience.kind === "all_users" ? 170 : campaign.audience.kind === "all_buyers" ? 128 : 42;
		// Отложенная рассылка: если availableAt в будущем, кампания переходит в
		// статус «scheduled», а не «sent» — как это делает бэкенд (worker отправит
		// её по времени). Иначе отправляем сразу.
		const scheduled = new Date(campaign.availableAt).getTime() > Date.now();
		const updated: NotificationCampaign = scheduled
			? { ...campaign, status: "scheduled", stats: { ...campaign.stats, targeted: count }, updatedAt: now() }
			: {
				...campaign,
				status: "sent",
				stats: { targeted: count, materialized: count, read: 0, unread: count, failed: 0 },
				sentAt: now(),
				updatedAt: now(),
			};
		campaigns = campaigns.map((entry) => entry.id === id ? updated : entry);
		return updated;
	},
	cancel: async (id) => {
		const campaign = await notificationCampaigns.get(id);
		const updated: NotificationCampaign = { ...campaign, status: "cancelled", cancelledAt: now(), updatedAt: now() };
		campaigns = campaigns.map((entry) => entry.id === id ? updated : entry);
		return updated;
	},
	stats: async (id) => {
		// Офлайн-заглушка: реальная статистика приходит с бэкенда.
		await wait();
		const campaign = campaigns.find((entry) => entry.id === id);
		return {
			campaignId: id,
			recipients: campaign?.stats.targeted ?? 0,
			delivered: campaign?.stats.materialized ?? 0,
			read: campaign?.stats.read ?? 0,
			readRate: 0,
			byTime: [],
		};
	},
	recipients: async ({ search = "" } = {}) => ({
		items: [
			{ id: "usr_mock_buyer", name: "Мария", email: "buyer@example.test", role: "buyer" },
			{ id: "usr_mock_seller", name: "Анна", email: "seller@example.test", role: "seller" },
		].filter((item) => `${item.name} ${item.email} ${item.id}`.toLowerCase().includes(search.toLowerCase())),
		nextCursor: null,
		hasMore: false,
	}),
	weeklyDigest: async () => {
		await wait();
		return { ...weeklyDigest };
	},
	updateWeeklyDigest: async (input) => {
		await wait();
		weeklyDigest = { ...input };
		return { ...weeklyDigest };
	},
};

// «Похожие подборки недели» (fd-notif-01): по умолчанию выключено, раз в 7 дней.
let weeklyDigest = { version: 1 as const, enabled: false, cadenceDays: 7 };
